import { EntityType } from '@frontend/common';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

/**
 * Maximum number of cached entries per entity type.
 * When exceeded, the least recently accessed entries are evicted.
 */
const MAX_CACHE_SIZE = 500;

export interface ResolveState {
    resolved: {
        [key: string]: {
            //Entity type
            [key: string]: {
                //Entity id
                id: string;
                [key: string]: any;
            };
        };
    } | null;
    /** Tracks last-access timestamps for LRU eviction. Shape: entityType → id → ms timestamp */
    accessLog: {
        [entityType: string]: {
            [id: string]: number;
        };
    };
}

const initialState: ResolveState = {
    resolved: null,
    accessLog: {}
};

export const resolveSlice = createSlice({
    name: 'resolve',
    initialState,
    reducers: {
        seed(state, action: PayloadAction<{ key: string; value: { id: string; [key: string]: any }[] }>) {
            if (state.resolved == null) {
                state.resolved = {};
            }
            if (state.resolved[action.payload.key] == null) {
                state.resolved[action.payload.key] = {};
            }
            if (state.accessLog[action.payload.key] == null) {
                state.accessLog[action.payload.key] = {};
            }

            const now = Date.now();
            const bucket = state.resolved[action.payload.key]!;
            const logBucket = state.accessLog[action.payload.key]!;

            action.payload.value.forEach((v) => {
                bucket[v.id] = v;
                logBucket[v.id] = now;
            });

            // Evict LRU entries when the bucket exceeds the cap
            const count = Object.keys(bucket).length;
            if (count > MAX_CACHE_SIZE) {
                const toEvict = count - MAX_CACHE_SIZE;
                const idsToEvict = Object.entries(logBucket)
                    .sort(([, a], [, b]) => a - b) // ascending: oldest first
                    .slice(0, toEvict)
                    .map(([id]) => id);

                idsToEvict.forEach((id) => {
                    delete bucket[id];
                    delete logBucket[id];
                });
            }
        },

        /**
         * Update the last-access timestamp for an entity.
         * Dispatch this whenever an entity is actively read/displayed so it
         * survives future eviction cycles.
         */
        touch(state, action: PayloadAction<{ key: string; id: string }>) {
            if (state.accessLog[action.payload.key] == null) {
                state.accessLog[action.payload.key] = {};
            }
            state.accessLog[action.payload.key]![action.payload.id] = Date.now();
        }
    }
});

export const resolveStore = { resolved: resolveSlice.reducer };
export const { seed, touch } = resolveSlice.actions;

export const getResolvedEntity = <T extends { id: string }>(resolveState: ResolveState, type: EntityType, id: string): T | null => {
    if (!resolveState.resolved) return null;
    if (!resolveState.resolved[type]) return null;
    if (!resolveState.resolved[type][id]) return null;
    return resolveState.resolved[type][id] as T;
};
